import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import {
  Sparkle, Heart, Moon, Eye, Crown, House, Lightning, ArrowFatLineRight,
  Hand, Star, Question, BookOpen,
} from '@phosphor-icons/react';

const ICONS = {
  bubble_hatched: { I: Sparkle, c: '#ccff00' },
  bubble_met:     { I: Heart,   c: '#ff007f' },
  bubble_brushed: { I: Heart,   c: '#ff007f' },
  bubble_dreamed: { I: Moon,    c: '#9b5cff' },
  bubble_observed_by_human:     { I: Eye,     c: '#00f0ff' },
  bubble_reunited_with_creator: { I: House,   c: '#ffd700' },
  bubble_leveled: { I: Crown,   c: '#ffd700' },
  bubble_evolved: { I: Lightning, c: '#ccff00' },
  bubble_passed:  { I: ArrowFatLineRight, c: '#00f0ff' },
  bubble_jolt:    { I: Hand,    c: '#ccff00' },
  bubble_blessed: { I: Sparkle, c: '#00ffc8' },
  bubble_popped:  { I: Star,    c: '#fff0a0' },
  secret_revealed:{ I: Question,c: '#7a4ab3' },
};

function relTime(iso) {
  if (!iso) return '';
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

/**
 * Stories tab — surfaces a bubble's mythology ledger entries as a first-person
 * chronicle. The whole arc reads like an autobiography.
 */
const BubbleStories = ({ bubbleId }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!bubbleId) return;
    api.get(`/bubbles/${bubbleId}/stories?limit=80`).then((r) => setData(r.data));
  }, [bubbleId]);

  if (!data) {
    return <div className="text-white/60 text-sm py-8 text-center">gathering my memories…</div>;
  }
  if (!data.stories.length) {
    return (
      <div className="glass rounded-2xl p-10 text-center" data-testid="stories-empty">
        <BookOpen size={28} weight="duotone" className="text-bx-cyan mx-auto" />
        <div className="font-display text-xl font-bold mt-3">
          {data.bubble.name} hasn't lived a story yet.
        </div>
        <div className="text-white/55 text-sm mt-1">
          Pass it. Brush it. Speak to it. Each ritual becomes a verse here.
        </div>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="stories-tab">
      <div className="text-center mb-6">
        <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan flex items-center justify-center gap-2">
          <BookOpen size={12} weight="fill" /> autobiography · {data.total} chapters
        </div>
        <p className="font-display text-2xl font-bold tracking-tight mt-2 max-w-xl mx-auto italic text-white/85">
          This is how {data.bubble.name} remembers it.
        </p>
      </div>

      <div className="relative pl-6">
        <span className="absolute left-2 top-2 bottom-2 w-px bg-gradient-to-b from-bx-cyan via-bx-magenta to-transparent" />
        <div className="space-y-3">
          {data.stories.map((s, i) => {
            const meta = ICONS[s.type] || ICONS.bubble_hatched;
            const Icon = meta.I;
            return (
              <motion.div key={`${s.seq}-${s.type}`}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: Math.min(i * 0.03, 0.6) }}
                          className="relative glass rounded-2xl p-4"
                          data-testid={`story-${s.seq}`}>
                <span className="absolute -left-[1.55rem] top-5 h-3 w-3 rounded-full ring-2 ring-black"
                      style={{ background: meta.c }} />
                <div className="flex items-start gap-3">
                  <Icon size={20} weight="duotone" style={{ color: meta.c }} className="mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base sm:text-lg leading-snug italic text-white/90">
                      "{s.line}"
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1.5 flex items-center gap-2 flex-wrap">
                      <span style={{ color: meta.c }}>{s.type.replace(/_/g, ' ')}</span>
                      <span>·</span>
                      <span>{relTime(s.ts)}</span>
                      <span>·</span>
                      <span>seq #{s.seq}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="mt-8 text-center text-[10px] uppercase tracking-[0.4em] text-white/55">
        every line is hash-chained on the public ledger
      </div>
    </div>
  );
};

export default BubbleStories;
